import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { chatAPI, CHAT_TOKEN_KEY } from '../api/modules/chat.js'

const ACTIVE_KEY = 'chat_active_uuid'
const FAST_POLL_MS = 4000
const SLOW_POLL_MS = 30000

/**
 * The customer's current support chat, shared by the floating widget and the
 * contact page. New messages are polled: every few seconds while a chat panel
 * is on screen, otherwise only the unread count every half a minute.
 */
export const useChatStore = defineStore('chat', () => {
  const isOpen = ref(false)
  const conversation = ref(null)
  const messages = ref([])
  const unreadCount = ref(0)
  const isLoading = ref(false)
  const isSending = ref(false)
  const error = ref(null)
  const fieldErrors = ref({})
  // the start form survives closing the panel
  // product: { id, name, variant_name, main_image } picked for "Spýtať sa k produktu"
  const draft = ref({ name: '', email: '', phone: '', topic: 'support', order_number: '', product: null, message: '' })
  // a product page asked about a product while another chat is still open
  const productPrompt = ref(null)

  const activeUuid = ref(localStorage.getItem(ACTIVE_KEY))
  const hasGuestToken = ref(!!localStorage.getItem(CHAT_TOKEN_KEY))

  // number of mounted chat panels; > 0 means poll fast
  const viewers = ref(0)
  let timer = null

  const lastId = computed(() => messages.value.length ? messages.value[messages.value.length - 1].id : 0)
  const isClosed = computed(() => conversation.value?.status === 'closed')
  const view = computed(() => {
    if (!conversation.value) return 'start'
    return isClosed.value ? 'ended' : 'thread'
  })

  const setActive = (uuid) => {
    activeUuid.value = uuid
    if (uuid) localStorage.setItem(ACTIVE_KEY, uuid)
    else localStorage.removeItem(ACTIVE_KEY)
  }

  const applyResponse = (response, { replace = false } = {}) => {
    if (response.data) conversation.value = response.data
    const incoming = response.messages || []
    if (replace) {
      messages.value = incoming
    } else {
      const known = new Set(messages.value.map(m => m.id))
      messages.value = [...messages.value, ...incoming.filter(m => !known.has(m.id))]
    }
  }

  const handleError = (err, fallback) => {
    fieldErrors.value = err.response?.data?.errors || {}
    error.value = err.response?.status === 429
      ? 'Príliš veľa pokusov, skúste to o chvíľu'
      : (err.response?.data?.message || fallback)
  }

  const open = () => {
    isOpen.value = true
    unreadCount.value = 0
  }

  const toggle = () => (isOpen.value ? (isOpen.value = false) : open())

  /** Resume the chat remembered in this browser */
  const restore = async () => {
    if (!activeUuid.value || conversation.value) return
    try {
      isLoading.value = true
      applyResponse(await chatAPI.get(activeUuid.value), { replace: true })
    } catch (err) {
      if (err.response?.status === 404) forget()
    } finally {
      isLoading.value = false
    }
  }

  const start = async () => {
    error.value = null
    fieldErrors.value = {}
    isSending.value = true
    try {
      const { product, ...payload } = draft.value
      if (payload.topic !== 'order') delete payload.order_number
      if (payload.topic === 'product') payload.product_id = product?.id
      const response = await chatAPI.start(payload)
      if (response.guest_token) {
        localStorage.setItem(CHAT_TOKEN_KEY, response.guest_token)
        hasGuestToken.value = true
      }
      applyResponse(response, { replace: true })
      setActive(response.data.uuid)
      draft.value.message = ''
      draft.value.order_number = ''
      draft.value.product = null
      if (draft.value.topic === 'product') draft.value.topic = 'support'
      schedule()
      return true
    } catch (err) {
      handleError(err, 'Správu sa nepodarilo odoslať')
      return false
    } finally {
      isSending.value = false
    }
  }

  const send = async (text) => {
    if (!conversation.value || !text.trim()) return false
    error.value = null
    isSending.value = true
    try {
      applyResponse(await chatAPI.send(conversation.value.uuid, text, lastId.value))
      return true
    } catch (err) {
      handleError(err, 'Správu sa nepodarilo odoslať')
      return false
    } finally {
      isSending.value = false
    }
  }

  const close = async () => {
    if (!conversation.value) return
    try {
      applyResponse(await chatAPI.close(conversation.value.uuid, lastId.value))
    } catch (err) {
      handleError(err, 'Konverzáciu sa nepodarilo ukončiť')
    }
  }

  const sendTranscript = async (email) => {
    error.value = null
    fieldErrors.value = {}
    try {
      const response = await chatAPI.transcript(conversation.value.uuid, email)
      if (response.data) conversation.value = response.data
      return response.message
    } catch (err) {
      handleError(err, 'Prepis sa nepodarilo odoslať')
      return null
    }
  }

  /** Leave the current chat and show the start form again */
  const startNew = () => {
    conversation.value = null
    messages.value = []
    error.value = null
    fieldErrors.value = {}
    productPrompt.value = null
    setActive(null)
  }

  const forget = () => {
    startNew()
  }

  /** Product page "Opýtať sa": open the start form with this product picked */
  const askAboutProduct = (product) => {
    const picked = {
      id: product.id,
      name: product.name,
      variant_name: product.variant_name || null,
      main_image: product.main_image || null,
    }
    if (view.value === 'thread') {
      // don't leave a running chat behind without asking
      productPrompt.value = picked
    } else {
      if (view.value === 'ended') startNew()
      draft.value.topic = 'product'
      draft.value.product = picked
    }
    open()
  }

  /** Answer to productPrompt: leave the open chat and ask about the product */
  const acceptProductPrompt = () => {
    const picked = productPrompt.value
    productPrompt.value = null
    if (!picked) return
    startNew()
    draft.value.topic = 'product'
    draft.value.product = picked
  }

  const poll = async () => {
    try {
      if (viewers.value > 0 && conversation.value) {
        applyResponse(await chatAPI.get(conversation.value.uuid, lastId.value))
      } else if (activeUuid.value || hasGuestToken.value || localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')) {
        const response = await chatAPI.unreadCount()
        unreadCount.value = response.data?.count || 0
      }
    } catch (err) {
      if (err.response?.status === 404 && conversation.value) forget()
    }
  }

  const schedule = () => {
    clearTimeout(timer)
    const delay = viewers.value > 0 && conversation.value ? FAST_POLL_MS : SLOW_POLL_MS
    timer = setTimeout(async () => {
      if (document.visibilityState === 'visible') await poll()
      schedule()
    }, delay)
  }

  /** A chat panel became visible: poll fast while it stays */
  const attach = () => {
    viewers.value++
    restore().then(() => {
      if (conversation.value) poll()
    })
    schedule()
  }

  const detach = () => {
    viewers.value = Math.max(0, viewers.value - 1)
    schedule()
  }

  /** Called after login / registration: move the guest's chats to the account */
  let claiming = Promise.resolve()
  const claimGuest = () => {
    if (!localStorage.getItem(CHAT_TOKEN_KEY)) return claiming
    claiming = chatAPI.claim()
      .then(() => {
        localStorage.removeItem(CHAT_TOKEN_KEY)
        hasGuestToken.value = false
        if (conversation.value) conversation.value = { ...conversation.value, is_guest: false }
      })
      .catch(() => {
        // keep the token, the next login can try again
      })
    return claiming
  }

  /** Resolves once a running claim finished (the account's chat list depends on it) */
  const whenClaimed = () => claiming

  /** On logout the chat belongs to the previous account */
  const reset = () => {
    startNew()
    unreadCount.value = 0
    isOpen.value = false
  }

  return {
    isOpen, conversation, messages, unreadCount, isLoading, isSending, error, fieldErrors, draft, productPrompt,
    activeUuid, view, isClosed, lastId,
    open, toggle, restore, start, send, close, sendTranscript, startNew, poll, schedule, attach, detach, claimGuest, whenClaimed, reset,
    askAboutProduct, acceptProductPrompt,
  }
})
